// @ts-check
/**
 * Custom elements for the OC product PDP section:
 *   <oc-gallery> — thumbs + main image + prev/next + hover-zoom side pane.
 *   <oc-qty>     — quantity stepper.
 *
 * The hover-zoom algorithm is a direct port of assets/hover-zoom.js (used by
 * the theme's product-information section). The magnified pane is portaled
 * to <body> so ancestor `overflow: hidden` (e.g. .oc-pdp__main) can't clip it.
 * On touch / narrow screens, the magnifier button opens a fullscreen <dialog>.
 *
 *   <oc-upsell> — "Complete your setup" cross-sell: checkbox rows whose checked
 *   prices sum into a live total, plus a batch "Add selected items" → /cart/add.js
 *   that dispatches CartAddEvent so the cart drawer/icon refresh like the main ATC.
 */

import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

const ZOOM_LEVEL = 2.5;
const PANE_GAP = 16;
const MIN_HOVER_ZOOM_WIDTH = 750;

const clamp = (/** @type {number} */ v, /** @type {number} */ min, /** @type {number} */ max) =>
  Math.max(min, Math.min(v, max));

/** True on touch-first devices (phones / tablets) — where the pointer is coarse and there's no hover. */
const isTouchDevice = () => window.matchMedia('(hover: none) and (pointer: coarse)').matches;

/**
 * @param {Touch} a
 * @param {Touch} b
 */
const touchDistance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

/**
 * Wires direct touch gestures onto a gallery viewport so phones/tablets get a native-feeling photo
 * viewer without the (removed) arrows + magnifier:
 *   - two-finger pinch zooms the active image about the pinch midpoint (focal-zoom math ported from
 *     drag-zoom-wrapper.js, but self-contained so this section doesn't depend on Horizon's zoom-dialog CSS),
 *   - a one-finger drag pans the image while it's zoomed in,
 *   - a horizontal flick at 1× flips photos (onSwipe),
 *   - a stationary tap fires onTap.
 * When `springBack` is set, the zoom animates back to 1× the moment all fingers lift (used for the inline
 * main image); otherwise the zoom persists (used inside the fullscreen gallery dialog).
 *
 * @param {HTMLElement} viewport - element that receives the touch events and clips the image
 * @param {object} opts
 * @param {() => HTMLImageElement | null} opts.getImage - current image to transform (null = skip zoom, e.g. video/3D)
 * @param {boolean} [opts.springBack] - reset zoom to 1× as soon as all fingers lift
 * @param {(dir: number) => void} [opts.onSwipe] - horizontal flick at 1× (dir = -1 prev / +1 next)
 * @param {() => void} [opts.onTap] - stationary single-finger tap
 * @param {number} [opts.maxScale]
 * @returns {{ reset: () => void }}
 */
function createGalleryTouch(viewport, opts) {
  const { getImage, onSwipe, onTap, springBack = false, maxScale = 4 } = opts;
  const SWIPE_THRESHOLD = 45;
  const MOVE_TOLERANCE = 10;

  let scale = 1;
  let tx = 0;
  let ty = 0;
  /** @type {'none'|'pinch'|'drag'|'swipe'|'scroll'|'tap'} */
  let mode = 'none';
  let pinched = false;
  let startDist = 0;
  let startScale = 1;
  let startX = 0;
  let startY = 0;
  let startTx = 0;
  let startTy = 0;
  let curDx = 0;
  let curDy = 0;
  /** @type {HTMLImageElement | null} */
  let img = null;

  const apply = () => {
    if (img) img.style.transform = `scale(${scale}) translate(${tx}px, ${ty}px)`;
  };

  // Keep a zoomed image from being dragged past its own edges (object-fit: contain aware).
  const constrain = () => {
    if (!img || scale <= 1) {
      tx = 0;
      ty = 0;
      return;
    }
    const rect = viewport.getBoundingClientRect();
    const nW = img.naturalWidth || rect.width;
    const nH = img.naturalHeight || rect.height;
    const ar = nW / nH;
    const vr = rect.width / rect.height;
    let iw;
    let ih;
    if (ar > vr) {
      iw = rect.width;
      ih = iw / ar;
    } else {
      ih = rect.height;
      iw = ih * ar;
    }
    const maxX = Math.max(0, iw * scale - rect.width) / 2 / scale;
    const maxY = Math.max(0, ih * scale - rect.height) / 2 / scale;
    tx = clamp(tx, -maxX, maxX);
    ty = clamp(ty, -maxY, maxY);
  };

  const springBackToBase = () => {
    const im = img;
    if (!im) return;
    scale = 1;
    tx = 0;
    ty = 0;
    im.style.transition = 'transform 0.25s ease';
    im.style.transform = 'scale(1) translate(0px, 0px)';
    let timer = 0;
    const cleanup = () => {
      im.style.transition = '';
      im.style.transform = '';
      im.removeEventListener('transitionend', cleanup);
      clearTimeout(timer);
    };
    timer = window.setTimeout(cleanup, 320);
    im.addEventListener('transitionend', cleanup);
  };

  /** @param {TouchEvent} event */
  const onStart = (event) => {
    if (event.touches.length === 1) {
      const t = event.touches[0];
      if (!t) return;
      img = getImage();
      if (img) img.style.transition = '';
      curDx = 0;
      curDy = 0;
      startX = t.clientX;
      startY = t.clientY;
      startTx = tx;
      startTy = ty;
      // Pan when already zoomed in (persistent dialog mode); otherwise a pending tap / swipe.
      mode = scale > 1.01 ? 'drag' : 'tap';
    } else if (event.touches.length === 2) {
      const t0 = event.touches[0];
      const t1 = event.touches[1];
      if (!t0 || !t1) return;
      img = getImage();
      if (!img) return; // nothing to pinch (video / 3D) — let native controls handle it
      img.style.transition = '';
      pinched = true;
      mode = 'pinch';
      startDist = touchDistance(t0, t1) || 1;
      startScale = scale;
      event.preventDefault();
    }
  };

  /** @param {TouchEvent} event */
  const onMove = (event) => {
    if (mode === 'pinch' && event.touches.length >= 2) {
      const t0 = event.touches[0];
      const t1 = event.touches[1];
      if (!t0 || !t1) return;
      const curDist = touchDistance(t0, t1) || 1;
      const rect = viewport.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const midX = (t0.clientX + t1.clientX) / 2;
      const midY = (t0.clientY + t1.clientY) / 2;
      const newScale = clamp((curDist / startDist) * startScale, 1, maxScale);
      // Shift translation so the pinch midpoint stays put as the scale changes.
      const scaleDelta = newScale / scale - 1;
      tx -= ((midX - cx) * scaleDelta) / newScale;
      ty -= ((midY - cy) * scaleDelta) / newScale;
      scale = newScale;
      constrain();
      apply();
      event.preventDefault();
      return;
    }
    if (event.touches.length !== 1) return;
    const t = event.touches[0];
    if (!t) return;
    curDx = t.clientX - startX;
    curDy = t.clientY - startY;

    if (mode === 'drag') {
      tx = startTx + curDx / scale;
      ty = startTy + curDy / scale;
      constrain();
      apply();
      event.preventDefault();
      return;
    }
    if (mode === 'tap') {
      if (Math.abs(curDx) > MOVE_TOLERANCE || Math.abs(curDy) > MOVE_TOLERANCE) {
        // Lock into a horizontal swipe, or yield to the browser for vertical page scroll.
        mode = Math.abs(curDx) > Math.abs(curDy) ? 'swipe' : 'scroll';
      }
    }
    if (mode === 'swipe') event.preventDefault();
  };

  /** @param {TouchEvent} event */
  const onEnd = (event) => {
    if (event.touches.length > 0) {
      // A finger lifted mid-pinch but others remain.
      if (mode === 'pinch') {
        const t = event.touches[0];
        if (springBack || !t) {
          mode = 'none';
        } else {
          startX = t.clientX;
          startY = t.clientY;
          startTx = tx;
          startTy = ty;
          mode = scale > 1.01 ? 'drag' : 'none';
        }
      }
      return;
    }
    if (pinched) {
      if (springBack) springBackToBase();
      else if (scale <= 1.01) {
        scale = 1;
        tx = 0;
        ty = 0;
        apply();
      }
    } else if (mode === 'tap') {
      onTap?.();
    } else if (mode === 'swipe' && Math.abs(curDx) > SWIPE_THRESHOLD) {
      onSwipe?.(curDx < 0 ? 1 : -1);
    }
    mode = 'none';
    pinched = false;
    curDx = 0;
    curDy = 0;
  };

  viewport.addEventListener('touchstart', onStart, { passive: false });
  viewport.addEventListener('touchmove', onMove, { passive: false });
  viewport.addEventListener('touchend', onEnd, { passive: false });
  viewport.addEventListener('touchcancel', onEnd, { passive: false });

  return {
    reset() {
      if (img) {
        img.style.transition = '';
        img.style.transform = '';
      }
      scale = 1;
      tx = 0;
      ty = 0;
      mode = 'none';
      pinched = false;
      curDx = 0;
      curDy = 0;
    },
  };
}

class OcGallery extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  thumbs = [];
  /** @type {HTMLLIElement[]} */
  slides = [];
  /** @type {number} */
  current = 0;
  /** @type {HTMLDialogElement | null} */
  zoomDialog = null;
  /** @type {HTMLImageElement | null} */
  zoomImage = null;
  /** @type {HTMLDivElement | null} */
  hoverPane = null;
  /** @type {HTMLImageElement | null} */
  hoverPaneImage = null;
  /** @type {HTMLLIElement | null} */
  hoverActiveSlide = null;
  /** @type {string | null} */
  hoverLastSrc = null;
  /** @type {boolean} */
  hoverActive = false;
  /** @type {{ reset: () => void } | null} */
  mainTouch = null;

  connectedCallback() {
    this.thumbs = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-thumb]')));
    this.slides = /** @type {HTMLLIElement[]} */ (Array.from(this.querySelectorAll('[data-slide]')));
    if (!this.slides.length) return;

    this.thumbs.forEach((/** @type {HTMLButtonElement} */ t, /** @type {number} */ i) => {
      t.addEventListener('click', () => this.activate(i));
    });

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.advance(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.advance(1));

    this.zoomDialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-zoom-dialog]'));
    this.zoomImage = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-zoom-image]'));
    this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.closeDialog());
    this.zoomDialog?.addEventListener('click', (e) => {
      if (e.target === this.zoomDialog || e.target === this.zoomImage) this.closeDialog();
    });

    // The zoom-open button routes to the image dialog for images, or to Horizon's <zoom-dialog>
    // (positioned at the active media index) for video / 3D — letting users open non-image media fullscreen
    // without hijacking clicks on the native video controls.
    const section = this.closest('section');
    const zoomId = section?.dataset.zoomId;
    this.querySelector('[data-zoom-open]')?.addEventListener('click', () => {
      const activeSlide = this.slides[this.current];
      const mediaType = activeSlide?.dataset.mediaType;
      if (mediaType && mediaType !== 'image' && zoomId) {
        const dialog = /** @type {any} */ (document.getElementById(zoomId));
        if (dialog && typeof dialog.open === 'function') {
          dialog.open(this.current);
          return;
        }
      }
      this.openDialog();
    });

    // "+N" overflow thumbs: open the OC gallery dialog (Amazon-style popup with full thumb strip),
    // styled with our design tokens, scoped to this section so multiple PDPs on a page can't collide.
    // There can be two — a desktop tile and a mobile tile (different start-index/count, toggled by CSS) —
    // so wire every one; only the breakpoint-visible tile is clickable at a time.
    const moreThumbs = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-thumb-more]')));
    moreThumbs.forEach((/** @type {HTMLButtonElement} */ moreThumb) => {
      moreThumb.addEventListener('click', () => {
        const galleryDialog = /** @type {OcGalleryDialog | null} */ (section?.querySelector('oc-gallery-dialog') ?? null);
        if (!galleryDialog) return;
        const startIndex = parseInt(moreThumb.getAttribute('data-start-index') || '0', 10);
        galleryDialog.open(Number.isFinite(startIndex) ? startIndex : 0);
      });
    });

    this.activate(0);
    this.initHoverZoom();
    this.initTouchGallery();
  }

  /**
   * Touch devices: replace the (hidden) arrows + magnifier with direct gestures on the main image —
   * horizontal swipe flips photos, a two-finger pinch zooms it in place (springing back on release),
   * and a single tap opens the fullscreen gallery (which has its own pinch-zoom).
   */
  initTouchGallery() {
    if (!isTouchDevice()) return;
    const main = /** @type {HTMLElement | null} */ (this.querySelector('.oc-pdp__main'));
    if (!main) return;
    this.mainTouch = createGalleryTouch(main, {
      getImage: () => {
        const active = this.slides[this.current];
        // Pinch only applies to static images; video / 3D keep their native controls.
        if (active?.dataset.mediaType && active.dataset.mediaType !== 'image') return null;
        return /** @type {HTMLImageElement | null} */ (active?.querySelector('.oc-pdp__slide-image') ?? null);
      },
      springBack: true,
      onSwipe: (dir) => this.advance(dir),
      onTap: () => {
        const active = this.slides[this.current];
        if (active?.dataset.mediaType && active.dataset.mediaType !== 'image') return;
        this.openMobileZoom();
      },
    });
  }

  /** Opens the fullscreen photo viewer for the active image (rich gallery dialog, or the simple zoom dialog as a single-image fallback). */
  openMobileZoom() {
    const section = this.closest('section');
    const galleryDialog = /** @type {OcGalleryDialog | null} */ (section?.querySelector('oc-gallery-dialog') ?? null);
    if (galleryDialog && typeof galleryDialog.open === 'function') {
      galleryDialog.open(this.current);
      return;
    }
    this.openDialog();
  }

  disconnectedCallback() {
    this.hoverPane?.remove();
    this.hoverPane = null;
    this.hoverPaneImage = null;
  }

  initHoverZoom() {
    if (window.innerWidth < MIN_HOVER_ZOOM_WIDTH) return;
    if (!window.matchMedia('(hover: hover)').matches) return;

    const pane = document.createElement('div');
    pane.className = 'oc-pdp__hz-pane';
    pane.setAttribute('aria-hidden', 'true');
    const paneImg = document.createElement('img');
    paneImg.className = 'oc-pdp__hz-pane-image';
    paneImg.alt = '';
    paneImg.decoding = 'async';
    paneImg.addEventListener('load', () => {
      if (this.hoverActive) this.updateHoverPanePosition();
    });
    pane.appendChild(paneImg);
    document.body.appendChild(pane);
    this.hoverPane = pane;
    this.hoverPaneImage = paneImg;

    this.slides.forEach((slide) => {
      // Skip non-image slides (video / 3D model) — hover-zoom only makes sense for static images.
      if (slide.dataset.mediaType && slide.dataset.mediaType !== 'image') return;
      const img = slide.querySelector('img');
      if (!img) return;

      const lens = document.createElement('div');
      lens.className = 'oc-pdp__hz-lens';
      lens.setAttribute('aria-hidden', 'true');
      slide.appendChild(lens);

      slide.addEventListener('pointerenter', (e) => this.handleHoverEnter(e, slide, img));
      slide.addEventListener('pointermove', (e) => this.handleHoverMove(e, slide));
      slide.addEventListener('pointerleave', () => this.handleHoverLeave(slide));
    });
  }

  updateHoverPanePosition() {
    if (!this.hoverPane || !this.hoverActiveSlide) return;
    // Pane is always square, matches the (square) gallery main area, and sits
    // to the right of it — independent of the image aspect ratio.
    const host = this.hoverActiveSlide.getBoundingClientRect();
    if (!host.width) return;
    const size = host.width;
    let left = host.right + PANE_GAP;
    if (left + size > window.innerWidth) {
      left = host.left - size - PANE_GAP;
    }
    this.hoverPane.style.setProperty('--hz-pane-top', `${host.top}px`);
    this.hoverPane.style.setProperty('--hz-pane-left', `${left}px`);
    this.hoverPane.style.setProperty('--hz-pane-width', `${size}px`);
    this.hoverPane.style.setProperty('--hz-pane-height', `${size}px`);
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} slide
   */
  updateHoverPosition(event, slide) {
    if (!this.hoverPane) return;
    const host = slide.getBoundingClientRect();
    if (host.width === 0 || host.height === 0) return;

    // Coordinates expressed relative to the square slide/main area. The image
    // inside is letterboxed via object-fit: contain, but the zoom math uses the
    // outer square so the pane stays square at any image aspect ratio.
    const x = ((event.clientX - host.left) / host.width) * 100;
    const y = ((event.clientY - host.top) / host.height) * 100;
    const halfLens = 50 / ZOOM_LEVEL;
    const xPct = clamp(x, halfLens, 100 - halfLens);
    const yPct = clamp(y, halfLens, 100 - halfLens);

    slide.style.setProperty('--zoom-x', `${xPct}%`);
    slide.style.setProperty('--zoom-y', `${yPct}%`);
    this.hoverPane.style.setProperty('--zoom-x', `${xPct}%`);
    this.hoverPane.style.setProperty('--zoom-y', `${yPct}%`);
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLLIElement} slide
   * @param {HTMLImageElement} img
   */
  handleHoverEnter(event, slide, img) {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    if (!this.hoverPane || !this.hoverPaneImage) return;
    const src =
      img.getAttribute('data-max-resolution') ||
      img.getAttribute('data-zoom') ||
      img.currentSrc ||
      img.src;
    if (!src) return;
    if (this.hoverLastSrc !== src) {
      this.hoverPaneImage.src = src;
      this.hoverLastSrc = src;
    }
    slide.style.setProperty('--zoom-level', String(ZOOM_LEVEL));
    this.hoverPane.style.setProperty('--zoom-level', String(ZOOM_LEVEL));
    slide.dataset.hoverActive = '';
    this.hoverPane.dataset.active = '';
    this.hoverActiveSlide = slide;
    this.hoverActive = true;
    this.updateHoverPanePosition();
    this.updateHoverPosition(event, slide);
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} slide
   */
  handleHoverMove(event, slide) {
    if (!this.hoverActive || this.hoverActiveSlide !== slide) return;
    this.updateHoverPanePosition();
    this.updateHoverPosition(event, slide);
  }

  /** @param {HTMLElement} slide */
  handleHoverLeave(slide) {
    if (this.hoverActiveSlide !== slide) return;
    delete slide.dataset.hoverActive;
    if (this.hoverPane) delete this.hoverPane.dataset.active;
    this.hoverActiveSlide = null;
    this.hoverActive = false;
  }

  /** @param {number} i */
  activate(i) {
    this.current = i;
    this.thumbs.forEach((t, j) => t.setAttribute('aria-pressed', j === i ? 'true' : 'false'));
    this.slides.forEach((s, j) => s.toggleAttribute('data-active', j === i));

    // Sync gallery main container with the active media: aspect ratio for non-image media (so 16:9 video isn't
    // letterboxed into a 1:1 square) and a data-active-media flag so CSS can reveal the magnifier on those slides.
    const main = /** @type {HTMLElement | null} */ (this.querySelector('.oc-pdp__main'));
    const active = this.slides[i];
    if (main && active) {
      const mediaType = active.dataset.mediaType;
      if (mediaType && mediaType !== 'image') {
        const aspect = parseFloat(active.dataset.aspectRatio || '');
        if (Number.isFinite(aspect) && aspect > 0) {
          main.style.setProperty('--main-aspect', String(aspect));
        } else {
          main.style.removeProperty('--main-aspect');
        }
        main.dataset.activeMedia = mediaType;
      } else {
        main.style.removeProperty('--main-aspect');
        main.dataset.activeMedia = 'image';
      }
    }

    const activeThumb = this.thumbs[i];
    activeThumb?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  /** @param {number} dir */
  advance(dir) {
    const next = (this.current + dir + this.slides.length) % this.slides.length;
    this.activate(next);
  }

  openDialog() {
    if (!this.zoomDialog || !this.zoomImage) return;
    const activeSlide = this.slides[this.current];
    const img = /** @type {HTMLImageElement | null} */ (activeSlide?.querySelector('img'));
    if (!img) return;

    const best =
      img.getAttribute('data-max-resolution') ||
      img.getAttribute('data-zoom') ||
      img.currentSrc ||
      img.src;
    this.zoomImage.src = best;
    this.zoomImage.alt = img.alt || '';
    this.zoomDialog.showModal();
  }

  closeDialog() {
    if (this.zoomDialog?.open) this.zoomDialog.close();
  }
}

class OcQty extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  input = null;
  /** @type {number} */
  min = 1;
  /** @type {number} */
  max = Number.POSITIVE_INFINITY;

  connectedCallback() {
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="number"]'));
    if (!this.input) return;
    this.min = parseInt(this.input.min || '1', 10);
    this.max = this.input.max ? parseInt(this.input.max, 10) : Number.POSITIVE_INFINITY;

    this.querySelector('[data-qty-dec]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-qty-inc]')?.addEventListener('click', () => this.step(1));
    this.input.addEventListener('change', () => this.clamp());
  }

  /** @param {number} dir */
  step(dir) {
    if (!this.input) return;
    const current = parseInt(this.input.value || `${this.min}`, 10);
    this.input.value = String(Math.max(this.min, Math.min(this.max, current + dir)));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  clamp() {
    if (!this.input) return;
    const v = parseInt(this.input.value || `${this.min}`, 10);
    if (Number.isNaN(v)) {
      this.input.value = String(this.min);
    } else {
      this.input.value = String(Math.max(this.min, Math.min(this.max, v)));
    }
  }
}

/**
 * <oc-gallery-dialog> — Amazon-style "see all photos" popup. Hosts a vertical thumbnail strip on the
 * left and an active image on the right, navigable via thumbs, prev/next, and arrow keys. Built on a
 * native <dialog> so the browser handles focus trapping, backdrop, and Escape.
 */
class OcGalleryDialog extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  /** @type {HTMLButtonElement[]} */
  thumbs = [];
  /** @type {HTMLLIElement[]} */
  slides = [];
  /** @type {HTMLElement | null} */
  counter = null;
  /** @type {number} */
  current = 0;
  /** @type {(event: KeyboardEvent) => void} */
  keydownHandler = () => {};
  /** @type {{ reset: () => void } | null} */
  touch = null;

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-gallery-dialog]'));
    this.thumbs = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-gallery-thumb]')));
    this.slides = /** @type {HTMLLIElement[]} */ (Array.from(this.querySelectorAll('[data-gallery-slide]')));
    this.counter = /** @type {HTMLElement | null} */ (this.querySelector('[data-gallery-counter]'));
    if (!this.dialog || !this.slides.length) return;

    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.activate(i)));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.advance(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.advance(1));
    this.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.close());

    // Click outside the inner content (i.e. on the dialog's padding box, which is the closest the
    // backdrop gets in event-target terms) closes the popup.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.keydownHandler = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.advance(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.advance(1);
      }
    };
    this.dialog.addEventListener('keydown', this.keydownHandler);
    this.initTouch();
  }

  /**
   * Touch devices: pinch-zoom / pan the active slide and swipe between photos inside the fullscreen
   * popup. The stage owns the touch surface (touch-action: none in CSS) so gestures never scroll the
   * page behind the modal.
   */
  initTouch() {
    if (!isTouchDevice()) return;
    const stage = /** @type {HTMLElement | null} */ (this.querySelector('.oc-pdp__gallery-dialog__stage'));
    if (!stage) return;
    this.touch = createGalleryTouch(stage, {
      getImage: () => {
        const active = this.slides[this.current];
        if (active?.dataset.mediaType && active.dataset.mediaType !== 'image') return null;
        return /** @type {HTMLImageElement | null} */ (active?.querySelector('img') ?? null);
      },
      onSwipe: (dir) => this.advance(dir),
    });
  }

  /** Clears any pinch-zoom transform left on the slide images (called on navigate / close). */
  resetZoom() {
    this.slides.forEach((s) => {
      const im = /** @type {HTMLImageElement | null} */ (s.querySelector('img'));
      if (im) {
        im.style.transition = '';
        im.style.transform = '';
      }
    });
    this.touch?.reset();
  }

  /** @param {number} index */
  open(index = 0) {
    if (!this.dialog) return;
    const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
    this.activate(clamped, { scrollBehavior: 'instant' });
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
    this.slides.forEach((s) => this.pauseSlideMedia(s));
    this.resetZoom();
  }

  /**
   * @param {number} i
   * @param {{ scrollBehavior?: ScrollBehavior }} options
   */
  activate(i, options = {}) {
    if (i < 0 || i >= this.slides.length) return;
    const prev = this.current;
    this.current = i;
    this.thumbs.forEach((t, j) => t.setAttribute('aria-pressed', j === i ? 'true' : 'false'));
    this.slides.forEach((s, j) => s.toggleAttribute('data-active', j === i));

    if (prev !== i) {
      this.pauseSlideMedia(this.slides[prev]);
      // Changing photos clears any pinch-zoom so the new one always starts at 1×.
      this.resetZoom();
    }

    const activeThumb = this.thumbs[i];
    activeThumb?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: options.scrollBehavior ?? 'smooth' });

    if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
  }

  /** @param {HTMLElement | undefined} slide */
  pauseSlideMedia(slide) {
    if (!slide) return;
    slide.querySelectorAll('video').forEach((v) => { try { v.pause(); } catch (e) { /* noop */ } });
    slide.querySelectorAll('iframe').forEach((f) => {
      // Reassigning src is the only cross-origin-safe way to stop YouTube/Vimeo playback.
      const src = f.getAttribute('src');
      if (src) f.setAttribute('src', src);
    });
  }

  /** @param {number} dir */
  advance(dir) {
    if (!this.slides.length) return;
    const next = (this.current + dir + this.slides.length) % this.slides.length;
    this.activate(next);
  }
}

/**
 * <oc-upsell> — "Complete your setup" cross-sell block.
 * Sums the prices of checked items into a live total and batch-adds the selected
 * variants to the cart in a single /cart/add.js request, then dispatches a
 * CartAddEvent so the cart drawer / icon (which listen on `document`) refresh.
 */
class OcUpsell extends HTMLElement {
  /** @type {HTMLInputElement[]} */
  checks = [];
  /** @type {HTMLElement | null} */
  totalEl = null;
  /** @type {HTMLButtonElement | null} */
  addBtn = null;
  /** @type {HTMLElement | null} */
  errorEl = null;
  /** @type {string} */
  moneyFormat = '${{amount}}';
  /** @type {string} */
  currency = 'USD';
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  addedTimer;
  /** @type {boolean} */
  busy = false;

  connectedCallback() {
    this.checks = /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('[data-upsell-check]')));
    this.totalEl = this.querySelector('[data-upsell-total]');
    this.addBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-upsell-add]'));
    this.errorEl = this.querySelector('[data-upsell-error]');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';

    this.checks.forEach((c) => c.addEventListener('change', () => this.update()));
    this.addBtn?.addEventListener('click', () => this.addToCart());
    this.update();
  }

  /** @returns {HTMLInputElement[]} checked, enabled rows with a variant id */
  selected() {
    return this.checks.filter((c) => c.checked && !c.disabled && c.value);
  }

  /** Recompute the live total and the CTA's disabled state. */
  update() {
    const sel = this.selected();
    let cents = 0;
    sel.forEach((c) => {
      cents += parseInt(c.dataset.price || '0', 10) || 0;
    });
    if (this.totalEl) this.totalEl.textContent = formatMoney(cents, this.moneyFormat, this.currency);
    if (this.addBtn && !this.busy) {
      this.addBtn.disabled = sel.length === 0;
    }
  }

  addToCart() {
    const sel = this.selected();
    if (!sel.length || !this.addBtn || this.busy) return;

    this.#hideError();
    this.busy = true;

    // Fire the visual feedback IMMEDIATELY — exactly like the main buy button, whose handleClick
    // plays the burst + fly-to-cart synchronously on click and lets the network request run in the
    // background. Awaiting the fetch first is what made this feel laggy. Fly unconditionally, the
    // same as this section's sticky bar (it intentionally doesn't gate on the add_to_cart_animation
    // setting, so the effect is consistent regardless of that toggle).
    this.#playAddedAnimation();
    sel.forEach((c) => this.#flyToCart(c));

    // The first row is the current product (the bundle anchor); add the variant that's actually
    // selected in the main form, not the one captured at render time, so variant switches are honored.
    const liveId = this.#currentVariantId();
    const items = sel.map((c) => ({
      id: c.hasAttribute('data-upsell-current') && liveId ? liveId : Number(c.value),
      quantity: 1,
    }));
    this.#submit(items);
  }

  /** @returns {number | null} the variant id currently selected in the section's main product form */
  #currentVariantId() {
    const input = this.closest('.oc-pdp')?.querySelector('product-form-component input[name="id"]');
    const val = input instanceof HTMLInputElement ? Number(input.value) : NaN;
    return Number.isFinite(val) && val > 0 ? val : null;
  }

  /**
   * Posts the selected variants to the cart in the background, then dispatches CartAddEvent so the
   * drawer/icon refresh. Errors surface in the inline error region (the optimistic animation already
   * played, matching the main button's behaviour).
   * @param {Array<{id: number, quantity: number}>} items
   */
  async #submit(items) {
    try {
      const addRes = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const addJson = await addRes.json().catch(() => null);

      if (!addRes.ok) {
        this.#showError(addJson?.description || addJson?.message || 'Could not add these items.');
        return;
      }

      // Pull the fresh cart so the drawer/icon get an accurate item_count; cosmetic, so
      // a failure here shouldn't block dispatching the update event.
      let cart;
      try {
        const cartRes = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        cart = await cartRes.json();
      } catch {
        /* noop */
      }

      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id || 'oc-pdp-upsell', {
          source: 'oc-pdp-upsell',
          itemCount: items.length,
        })
      );
    } catch (err) {
      console.error('[oc-upsell] add to cart failed', err);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      this.busy = false;
      this.update();
    }
  }

  /**
   * Replays the main buy button's checkmark-burst by toggling [data-added] — the animation itself
   * lives in base.css and keys off `.add-to-cart-button[data-added='true']`, so this is the exact
   * same feedback as the section's primary Add-to-cart (and the sticky bar's proxy button).
   */
  #playAddedAnimation() {
    if (!this.addBtn) return;
    this.addBtn.dataset.added = 'true';
    clearTimeout(this.addedTimer);
    this.addedTimer = setTimeout(() => {
      this.addBtn?.removeAttribute('data-added');
    }, 1000);
  }

  /**
   * Spawns a <fly-to-cart> that arcs the item's thumbnail into the header cart icon. Mirrors this
   * section's proven sticky-bar fly (useSourceSize + a customElements.get guard) rather than the
   * main button's private path, so it works the same way the rest of the section already does.
   * @param {HTMLInputElement} check - the row's checkbox (used to locate the item + its image)
   */
  #flyToCart(check) {
    if (!window.customElements || !customElements.get('fly-to-cart')) return;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon) return;

    const item = check.closest('.oc-pdp__upsell-item');
    const img = /** @type {HTMLImageElement | null} */ (item?.querySelector('.oc-pdp__upsell-photo img') ?? null);
    const source = img || item || this.addBtn;
    if (!source) return;

    const fly = /** @type {any} */ (document.createElement('fly-to-cart'));
    fly.classList.add('fly-to-cart--main');
    const image = img && (img.currentSrc || img.src);
    if (image) fly.style.setProperty('background-image', `url(${image})`);
    fly.useSourceSize = 'true';
    fly.source = source;
    fly.destination = cartIcon;
    document.body.appendChild(fly);
  }

  /** @param {string} msg */
  #showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  #hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }
}

if (!customElements.get('oc-gallery')) customElements.define('oc-gallery', OcGallery);
if (!customElements.get('oc-gallery-dialog')) customElements.define('oc-gallery-dialog', OcGalleryDialog);
if (!customElements.get('oc-qty')) customElements.define('oc-qty', OcQty);
if (!customElements.get('oc-upsell')) customElements.define('oc-upsell', OcUpsell);
